/**
 * Live table state, driven entirely by server messages (the client never mutates game state locally —
 * it only renders the latest authoritative view and sends intents).
 */
import { create } from 'zustand';
import type {
  ChatMessagePayload,
  GameResultPayload,
  GameStatePayload,
  MatchmakingStatusPayload,
  SeatPublic,
  TableInfo,
  TableSnapshotPayload,
  TurnInfo,
} from '@cardhub/protocol';
import { api, ApiError } from '../api/client';
import { uuid } from '../lib/device';
import { sfx } from '../lib/sound';
import { haptic } from '../native/platform';
import { useRecentGames } from './recentGames';
import { realtime, useSession } from './session';
import { toast } from './ui';
import { OFFLINE_TABLE_ID, isOffline, isOfflineTable, leaveOfflineGame, offlineAct, startOfflineTable } from '../offline/offline-game';
import { cachedTable, quickPlayTable } from '../offline/catalog';

export interface GameEvent {
  type: string;
  seat?: number;
  [k: string]: unknown;
}

interface GameState {
  table: TableInfo | null;
  mySeat: number | null;
  seats: SeatPublic[];
  view: unknown;
  legal: unknown;
  turn: TurnInfo | null;
  seq: number;
  events: GameEvent[];
  eventsId: number;
  chat: ChatMessagePayload[];
  result: GameResultPayload | null;
  mm: MatchmakingStatusPayload | null;
  closed: { tableId: string; reason: string; chips?: number } | null;
  acting: boolean;
  lastError: string | null;
  quickPlay(tableConfigId: string): Promise<boolean>;
  quickPlayGame(gameType: string): Promise<boolean>;
  cancelMatchmaking(): Promise<void>;
  act(action: Record<string, unknown>): Promise<boolean>;
  leave(): Promise<void>;
  imBack(): void;
  sendChat(p: { text?: string; preset?: string; emote?: string }): Promise<void>;
  open(tableId: string, spectate?: boolean): Promise<void>;
  clearResult(): void;
  reset(): void;
}

const initial = {
  table: null,
  mySeat: null,
  seats: [],
  view: null,
  legal: null,
  turn: null,
  seq: 0,
  events: [],
  eventsId: 0,
  chat: [],
  result: null,
  mm: null,
  closed: null,
  acting: false,
  lastError: null,
};

// Matchmaking cancel bookkeeping: statuses that arrive just after a cancel are stale and ignored, and a
// cancel pressed while the join request is still in flight is re-sent once the join lands.
let cancelledAt = 0;
let joinInFlight = false;
/** Last matchmaking status from the server (it sends one about every second while searching). */
let lastMmStatusAt = 0;
let watchdog: ReturnType<typeof setInterval> | null = null;

/**
 * While searching, the server reports progress every ~1 s. If that stops (a dead socket on a phone,
 * a lost "match found"), recover instead of spinning forever: open the table if we were seated,
 * otherwise reconnect and search again.
 */
function startMatchmakingWatchdog(get: () => GameState, set: (p: Partial<GameState>) => void): void {
  if (watchdog) clearInterval(watchdog);
  lastMmStatusAt = Date.now();
  let recovering = false;
  watchdog = setInterval(() => {
    const mm = get().mm;
    if (!mm || mm.state !== 'SEARCHING') {
      if (watchdog) clearInterval(watchdog);
      watchdog = null;
      return;
    }
    if (recovering || joinInFlight || Date.now() - lastMmStatusAt < 5000) return;
    recovering = true;
    void (async () => {
      try {
        const cur = await api.get<{ tableId: string } | null>('/api/v1/tables/current').catch(() => null);
        if (get().mm?.state !== 'SEARCHING') return;
        if (cur?.tableId) {
          set({ mm: { ...mm, state: 'MATCHED', tableId: cur.tableId } });
          return;
        }
        realtime.forceReconnect();
        if (!(await realtime.whenOpen(8000))) return;
        if (get().mm?.state !== 'SEARCHING' || !mm.tableConfigId) return;
        const r = await realtime.request('mm.join.v1', { tableConfigId: mm.tableConfigId, pingMs: Math.round(realtime.rttMs) });
        const data = r.data as { status?: string; tableId?: string } | undefined;
        if (r.ok && data?.status === 'SEATED' && data.tableId) set({ mm: { ...mm, state: 'MATCHED', tableId: data.tableId } });
        else if (!r.ok) {
          set({ mm: null });
          toast(r.error?.message ?? "Couldn't reach the server — try again", 'error');
        }
      } finally {
        lastMmStatusAt = Date.now();
        recovering = false;
      }
    })();
  }, 1500);
}
let cancelWhileJoining = false;
async function sendCancel(): Promise<void> {
  const r = await realtime.request('mm.cancel.v1', {}, 3000);
  // The socket may be reconnecting (phones often are): confirm over HTTP so the server really stops.
  if (!r.ok) await api.del('/api/v1/matchmaking').catch(() => undefined);
}

/** No connection: sit down at the table in a local game against bots instead (settled when we are back online). */
function playOffline(table: Parameters<typeof startOfflineTable>[0] | null): boolean {
  if (!table) {
    toast('This table is not available offline yet. Connect once to download the tables.', 'error');
    return false;
  }
  if (!startOfflineTable(table)) return false;
  useGame.setState({ mm: { state: 'MATCHED', tableConfigId: table.id, elapsedMs: 0, timeoutMs: 0, playersFound: 0, playersNeeded: 0, tableId: OFFLINE_TABLE_ID } });
  return true;
}

export const useGame = create<GameState>((set, get) => ({
  ...initial,

  async quickPlay(tableConfigId) {
    if (isOffline()) return playOffline(cachedTable(tableConfigId));
    set({ mm: { state: 'SEARCHING', tableConfigId, elapsedMs: 0, timeoutMs: 10000, playersFound: 1, playersNeeded: 4 }, result: null, closed: null });
    cancelledAt = 0;
    joinInFlight = true;
    cancelWhileJoining = false;
    let r: Awaited<ReturnType<typeof realtime.request>>;
    try {
      r = await realtime.request('mm.join.v1', { tableConfigId, pingMs: Math.round(realtime.rttMs) });
    } catch (e) {
      // Never leave the search overlay spinning on an unexpected client error.
      joinInFlight = false;
      set({ mm: null });
      toast(e instanceof Error ? e.message : 'Could not start matchmaking', 'error');
      return false;
    }
    joinInFlight = false;
    if (cancelWhileJoining) {
      if (r.ok) await sendCancel();
      return false;
    }
    // Already seated somewhere (e.g. a match you left the screen of): go straight back to it.
    const joined = r.data as { status?: string; tableId?: string } | undefined;
    if (r.ok && joined?.status === 'SEATED' && joined.tableId) {
      if (get().table?.tableId === joined.tableId) toast('You are still in a game. Finish it or leave the table first.', 'info');
      set({ mm: { state: 'MATCHED', tableConfigId, elapsedMs: 0, timeoutMs: 0, playersFound: 0, playersNeeded: 0, tableId: joined.tableId } });
      return true;
    }
    if (r.ok) startMatchmakingWatchdog(get, set);
    if (!r.ok) {
      set({ mm: null });
      toast(r.error?.message ?? 'Could not join', 'error');
      return false;
    }
    return true;
  },

  async quickPlayGame(gameType) {
    if (isOffline()) return playOffline(quickPlayTable(gameType, useSession.getState().balance));
    set({ mm: { state: 'SEARCHING', tableConfigId: '', elapsedMs: 0, timeoutMs: 10000, playersFound: 1, playersNeeded: 4 }, result: null, closed: null });
    cancelledAt = 0;
    joinInFlight = true;
    cancelWhileJoining = false;
    try {
      const r = await api.post<{ status: string; tableId?: string; tableConfigId: string }>('/api/v1/matchmaking/quick-play', { gameType, pingMs: Math.round(realtime.rttMs) });
      joinInFlight = false;
      if (cancelWhileJoining) {
        if (r.status !== 'SEATED') await sendCancel();
        else if (r.tableId) set({ mm: { state: 'MATCHED', tableConfigId: r.tableConfigId, elapsedMs: 0, timeoutMs: 0, playersFound: 0, playersNeeded: 0, tableId: r.tableId } });
        return false;
      }
      if (r.status === 'SEATED' && r.tableId && get().table?.tableId === r.tableId) toast('You are still in a game. Finish it or leave the table first.', 'info');
      if (r.status === 'SEATED' && r.tableId) set({ mm: { state: 'MATCHED', tableConfigId: r.tableConfigId, elapsedMs: 0, timeoutMs: 0, playersFound: 0, playersNeeded: 0, tableId: r.tableId } });
      else {
        // Only while still searching: the table may already have started (snapshot cleared the overlay).
        const cur = get().mm;
        if (cur?.state === 'SEARCHING') {
          set({ mm: { ...cur, tableConfigId: r.tableConfigId } });
          startMatchmakingWatchdog(get, set);
        }
      }
      return true;
    } catch (e) {
      joinInFlight = false;
      set({ mm: null });
      if (cancelWhileJoining) return false;
      toast(e instanceof ApiError ? e.message : 'Could not start matchmaking', 'error');
      return false;
    }
  },

  async cancelMatchmaking() {
    // Close at once; the server is told in the background.
    cancelledAt = Date.now();
    set({ mm: null });
    if (joinInFlight) {
      cancelWhileJoining = true;
      return;
    }
    await sendCancel();
  },

  async act(action) {
    const t = get().table;
    if (!t || get().acting) return false;
    if (isOfflineTable(t.tableId)) return offlineAct(action);
    set({ acting: true, lastError: null });
    const r = await realtime.request('game.action.v1', { tableId: t.tableId, actionId: uuid(), action, stateSeq: get().seq });
    set({ acting: false });
    if (!r.ok) {
      sfx.error();
      set({ lastError: r.error?.message ?? 'Action rejected' });
      if (r.error?.code !== 'NETWORK') toast(r.error?.message ?? 'Not allowed', 'error');
      return false;
    }
    return true;
  },

  async leave() {
    const t = get().table;
    if (t && isOfflineTable(t.tableId)) leaveOfflineGame();
    else if (t) await realtime.request('table.leave.v1', { tableId: t.tableId });
    get().reset();
  },

  imBack() {
    const t = get().table;
    const seat = get().mySeat;
    if (!t || seat === null || isOfflineTable(t.tableId)) return;
    // Optimistic: hide the auto-play banner at once; the server's seat update confirms it moments later.
    const setBotControlled = (v: boolean) => set({ seats: get().seats.map((x) => (x.seat === seat ? { ...x, botControlled: v } : x)) });
    setBotControlled(false);
    void (async () => {
      const send = () => realtime.request('table.ready.v1', { tableId: t.tableId }, 2500);
      let r: Awaited<ReturnType<typeof send>>;
      if (realtime.looksStale) {
        // The phone slept and the socket is dead: don't wait for a timeout — reconnect now (that alone
        // restores control on the server) and then confirm.
        realtime.forceReconnect();
        r = (await realtime.whenOpen(8000)) ? await send() : { ok: false, error: { code: 'NETWORK', message: 'Not connected' } };
      } else {
        r = await send();
        if (!r.ok && (r.error?.code === 'NETWORK' || r.error?.code === 'TIMEOUT')) {
          realtime.forceReconnect();
          if (await realtime.whenOpen(8000)) r = await send();
        }
      }
      if (!r.ok) {
        setBotControlled(true);
        toast(r.error?.code === 'NETWORK' || r.error?.code === 'TIMEOUT' ? "Can't reach the server — check your connection" : (r.error?.message ?? 'Could not resume'), 'error');
      }
    })();
  },

  async sendChat(p) {
    const t = get().table;
    if (!t || isOfflineTable(t.tableId)) return;
    const r = await realtime.request('chat.send.v1', { tableId: t.tableId, ...p });
    if (!r.ok) toast(r.error?.message ?? 'Message not sent', 'error');
  },

  async open(tableId, spectate = false) {
    if (get().table?.tableId === tableId) return;
    if (isOfflineTable(tableId)) {
      // A local match only lives in memory: after a reload there is nothing to rejoin.
      set({ closed: { tableId, reason: 'TABLE_CLOSED' } });
      return;
    }
    const r = await realtime.request('table.join.v1', spectate ? { tableId, spectate: true } : { tableId });
    if (!r.ok) {
      set({ closed: { tableId, reason: r.error?.code ?? 'TABLE_CLOSED' } });
    }
  },

  clearResult() {
    set({ result: null });
  },

  reset() {
    set({ ...initial });
  },
}));

// ---------------------------------------------------------------- server → store wiring

let wired = false;
export function wireGameStore(): void {
  if (wired) return;
  wired = true;
  const set = useGame.setState;
  const get = useGame.getState;

  realtime.on('mm.status.v1', (d) => {
    const s = d as MatchmakingStatusPayload;
    lastMmStatusAt = Date.now();
    // Just cancelled: ignore "still searching" updates that were already on their way.
    if (cancelledAt && Date.now() - cancelledAt < 5000 && s.state === 'SEARCHING') return;
    if (s.state === 'CANCELLED') set({ mm: null });
    else if (s.state === 'FAILED') {
      set({ mm: null });
      toast(
        s.reason === 'INSUFFICIENT_BALANCE' ? 'Not enough coins for this table.' : s.reason === 'NO_PLAYERS' ? 'No players found right now. Try again soon.' : "We couldn't start that table.",
        'error',
      );
    } else set({ mm: s });
  });

  realtime.on('table.snapshot.v1', (d) => applySnapshot(d as TableSnapshotPayload));

  realtime.on('game.state.v1', (d) => applyGameState(d as GameStatePayload));

  realtime.on('table.seats.v1', (d) => {
    const s = d as { tableId: string; seats: SeatPublic[] };
    if (get().table?.tableId === s.tableId) set({ seats: s.seats });
  });

  realtime.on('game.result.v1', (d) => applyResult(d as GameResultPayload));

  realtime.on('chat.message.v1', (d) => {
    const m = d as ChatMessagePayload;
    if (get().table?.tableId !== m.tableId) return;
    set({ chat: [...get().chat.slice(-60), m] });
  });

  realtime.on('table.closed.v1', (d) => {
    const c = d as { tableId: string; reason: string; chips?: number };
    if (get().table?.tableId === c.tableId) set({ closed: c });
  });

  // After a reconnect, ask for anything we missed on the current table.
  realtime.onReconnected(() => {
    const t = get().table;
    if (t && !isOfflineTable(t.tableId)) void realtime.request('table.resume.v1', { tableId: t.tableId, lastSeq: get().seq });
  });
}

/** Applies a full table snapshot (from the server, or built locally for an offline match). */
export function applySnapshot(s: TableSnapshotPayload): void {
  const get = useGame.getState;
  const isNew = get().table?.tableId !== s.table.tableId;
  // A game running on this device owns the screen: a server table (e.g. one we were seated at before the
  // connection dropped) must not replace it when the connection comes back.
  const local = get().table;
  if (local && isOfflineTable(local.tableId) && isNew && !get().result && !get().closed) return;
  useGame.setState({
    table: s.table,
    mySeat: s.mySeat,
    seats: s.seats,
    view: s.view,
    legal: s.legal,
    turn: s.turn,
    seq: s.seq,
    mm: null,
    closed: null,
    ...(isNew ? { chat: [], result: null, events: [{ type: 'snapshot' }], eventsId: get().eventsId + 1 } : {}),
  });
  if (isNew) {
    sfx.deal();
    // Only games the player actually sits down at count as played (not spectating, not the offline practice).
    const me = useSession.getState().user?.id;
    if (me && s.mySeat !== null && !isOfflineTable(s.table.tableId)) useRecentGames.getState().record(me, s.table.gameType);
  }
}

/** Applies one game-state update (from the server, or from the local offline engine). */
export function applyGameState(s: GameStatePayload): void {
  const get = useGame.getState;
  const cur = get();
  if (!cur.table || cur.table.tableId !== s.tableId) return;
  if (s.seq <= cur.seq) return; // duplicate/replayed
  if (s.seq > cur.seq + 1 && !isOfflineTable(s.tableId)) void realtime.request('table.resume.v1', { tableId: s.tableId, lastSeq: cur.seq });
  const myTurnBefore = cur.mySeat !== null && cur.turn?.seats.includes(cur.mySeat);
  useGame.setState({ view: s.view, legal: s.legal, turn: s.turn, seq: s.seq, events: s.events as GameEvent[], eventsId: cur.eventsId + 1 });
  for (const e of s.events) {
    if (e.type === 'cardPlayed' || e.type === 'discarded' || e.type === 'drew') sfx.card();
    if (['chaal', 'raise', 'action', 'bid'].includes(e.type)) sfx.chip();
    if (e.type === 'dealt' || e.type === 'handStart') sfx.deal();
  }
  const myTurnNow = cur.mySeat !== null && s.turn.seats.includes(cur.mySeat);
  if (myTurnNow && !myTurnBefore) {
    sfx.turn();
    if (hapticsEnabled()) void haptic('medium');
  }
}

/** Shows a finished match (server-settled, or the provisional result of an offline match). */
export function applyResult(r: GameResultPayload): void {
  if (useGame.getState().table?.tableId !== r.tableId) return;
  useGame.setState({ result: r });
  if (r.me) {
    if (r.me.coinsDelta > 0) sfx.win();
    else if (r.me.coinsDelta < 0) sfx.lose();
  }
  void useSession.getState().refreshMe().catch(() => undefined);
}

function hapticsEnabled(): boolean {
  try {
    return JSON.parse(localStorage.getItem('th.settings') ?? '{}').haptics !== false;
  } catch {
    return true;
  }
}
